/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React from 'react';
import styled, { css } from 'styled-components';
import { Field } from 'formik';
import PropTypes from 'prop-types';

import RadioButton from './RadioButton';
import { mediaHelper } from 'utils';
import { FontWeight, Text } from 'components/Text';
import Colors from 'constants/Colors';
import ValidationError from '../ValidationError';
import Label from '../Label';
import Tooltip from '../../Tooltip';
import { Icon } from 'components';

const propTypes = {
  fieldName: PropTypes.string,
  options: PropTypes.array,
  onChange: PropTypes.func,
  label: PropTypes.string,
  row: PropTypes.bool,
};

const defaultProps = {
  fieldName: PropTypes.string,
  options: PropTypes.array,
  onChange: "",
  label: null, 
  validation: true
};

const StyledRadioButton = styled(RadioButton)`
  margin-bottom: 8px;

  &:last-child {
    margin-right: 0;
    margin-bottom: 0;
  }
  ${mediaHelper.sm`
    margin-bottom: 0;
    margin-right: 24px;
  `}

  ${({ warning, warningByHiddenQuestions }) =>
    (warning || warningByHiddenQuestions) &&
      css`
        i {color: #FDC830};
        span {color: #FDC830};
    `}

  ${({ refused, refusedByHiddenQuestions }) =>
    (refused || refusedByHiddenQuestions) &&
    css`
        i {color: #DB0F16};
        span {color: #DB0F16};
    `}
  `;

const RadioButtonGroupContainer = styled.div`
  display: flex;
  flex-direction: column;

  ${({ inline }) =>
    inline &&
    css`
    width: 60%;
    min-width: 60%
  `}

  ${({ row }) => row ? `flex-direction: row;` : `flex-direction: column;`}
`;

const Wrapper = styled.div`
  width: 100%;

  ${({ inline }) =>
    inline &&
    css`
      display: flex;
      justify-content: flex-start;
      flex-wrap: wrap;
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.6;
    `}
`;

const RadioButtonGroup = ({
  warning,
  fieldName,
  options,
  onChange,
  label,
  disabled,
  readOnly,
  isRequired,
  hideLabelAsterisk,
  validationMinHeight,
  inline,
  validation,
  additionalInfo,
  infoContent,
  refused,
  warningByHiddenQuestions,
  refusedByHiddenQuestions,
  form: { touched, errors },
  ...props
}) => {
  const meta = {
    touched: touched[fieldName],
    error: errors[fieldName]
  };

  return (
    <Wrapper
      inline={inline}
      disabled={disabled}
      {...props}
    >
   

      {label && (
        <Label
          meta={meta}
          width={inline ? 35 : null}
          isRequired={isRequired}
          weight={FontWeight.SemiBold}
          inline={inline}
          hideLabelAsterisk={hideLabelAsterisk}
          infoContent={infoContent}
        >
          {label}
          {infoContent &&
            <Tooltip text={infoContent} infoContent={infoContent}>
              <Icon name='info' />
            </Tooltip>
          }
        </Label>
      )}

      {additionalInfo && (
        <Label>
          <sup>{additionalInfo}</sup>
        </Label>
      )}

      <RadioButtonGroupContainer inline={inline} {...props}>
        {options.map((el, index) => {
          return <Field
            disabled={disabled}
            key={index}
            readOnly={readOnly}
            name={fieldName}
            option={el.value}
            label={el.label}
            component={StyledRadioButton}
            tabIndex={el.tabIndex}
            onChange={onChange}
            warning={el.value.idAnswerOption === warning}
            refused={el.value.idAnswerOption === refused}
            warningByHiddenQuestions={warningByHiddenQuestions}
            refusedByHiddenQuestions={refusedByHiddenQuestions || meta?.error}
          />
        })}
      </RadioButtonGroupContainer>
      {validation && (
        <ValidationError
          style={{ minWidth: '100%' }}
          meta={meta}
          validationMinHeight={validationMinHeight}
        />
      )}    </Wrapper>
  );
};

RadioButtonGroup.propTypes = propTypes;
RadioButtonGroup.defaultProps = defaultProps;

export default RadioButtonGroup;
